import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Seal } from "@/components/Seal";
import { useBranding } from "@/lib/branding";

export function GiveSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const b = useBranding();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setAmount(data.amount);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (tries >= 6) { setState("pending"); return; }
        setTimeout(poll, 2000);
      } catch { setState("error"); }
    };
    poll();
  }, [sid]);

  return (
    <Shell>
      {state === "checking" && <><Loader2 className="h-12 w-12 text-[#C8A24C] animate-spin mx-auto" /><h1 className="font-serif text-2xl mt-4">Confirming your gift…</h1></>}
      {state === "paid" && <><CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto" /><h1 className="font-serif text-3xl mt-4">Thank you!</h1><p className="text-white/70 mt-2">Your gift{amount ? ` of $${amount}` : ""} to {b.university_name} has been received. A receipt is on its way.</p></>}
      {state === "pending" && <><Loader2 className="h-12 w-12 text-[#C8A24C] animate-spin mx-auto" /><h1 className="font-serif text-2xl mt-4">Payment processing</h1><p className="text-white/70 mt-2">Your payment is being finalized — you'll receive a confirmation shortly.</p></>}
      {state === "error" && <><XCircle className="h-14 w-14 text-red-400 mx-auto" /><h1 className="font-serif text-2xl mt-4">Something went wrong</h1><p className="text-white/70 mt-2">We couldn't confirm this gift. Please try again.</p></>}
      <Link to="/give" data-testid="give-again" className="inline-block mt-6 px-5 py-2.5 rounded-md bg-[#C8A24C] text-[#001A4B] font-bold">Back to Giving</Link>
    </Shell>
  );
}

export function GiveCancel() {
  return (
    <Shell>
      <XCircle className="h-14 w-14 text-white/50 mx-auto" />
      <h1 className="font-serif text-2xl mt-4">Gift canceled</h1>
      <p className="text-white/70 mt-2">No charge was made. We'd love your support whenever you're ready.</p>
      <Link to="/give" className="inline-block mt-6 px-5 py-2.5 rounded-md bg-[#C8A24C] text-[#001A4B] font-bold">Try Again</Link>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#001A4B] text-white flex flex-col items-center justify-center px-6 text-center">
      <Seal className="h-14 w-14 mb-6 mx-auto" />
      <div className="max-w-md">{children}</div>
    </div>
  );
}
